import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, withRouter } from 'react-router-dom';
import ediotrHeaderStyle from 'pages/Editor/EditorHeader/EditorHeader.module.scss';

import Editor from 'components/common/DrawingEditor/DrawingEditor';
import TextField from 'components/common/TextField/TextField';
import requestAPI from 'utils/apiHandler';

import ExportReview from './Alerts/ExportReview/ExportReview';
import PrintThumbnail from './PrintReviews/PrintThumbnail';
import styles from './Results.module.scss';
import './custom-editor.css';
import { AiReviewFullDetails, ReviewContent } from './types';

interface ResultsProps {
  match: { params: { id: string } };
}

const Results = ({ match }: ResultsProps) => {
  const [data, setData] = useState<AiReviewFullDetails>();
  const [selectedReview, setSelectedReview] = useState<ReviewContent>();
  const { t } = useTranslation();
  const loadData = () => {
    requestAPI(`/review/${match.params.id}`).then(response => setData(response));
  };
  const finalResult = useMemo(() => {
    if (!data) return [] as ReviewContent[];
    const raw = data.content.successfulReviews.content;
    const real = Array.isArray(raw) ? raw as ReviewContent[] : Object.values(raw) as ReviewContent[];
    setSelectedReview(real[0]);
    return real;
  }, [data]);

  useEffect(loadData, []);

  const router = useHistory();
  return (
    <div>
      <div className={ediotrHeaderStyle.container}>
        <div className={ediotrHeaderStyle.documentLocationSection}>
          <div className={ediotrHeaderStyle.documentLocation}>
            <div className={ediotrHeaderStyle.locationItem}>
              <span
                onClick={() => router.push('/ai/review')}
                className={ediotrHeaderStyle.folderName}
              >
                {t('aireview:AI_Document_Review')}
              </span>
              <span className={ediotrHeaderStyle.slash}>/</span>
            </div>

            <span
              onClick={() => setSelectedReview(finalResult[0])}
              className={ediotrHeaderStyle.folderName}
            >
              {data && data.subject.title} {data && data.class.title}
            </span>
            {selectedReview && (
              <>
                <span className={ediotrHeaderStyle.slash}>/</span>

                <span className={ediotrHeaderStyle.fileName}>{selectedReview.name}</span>
              </>
            )}
          </div>
        </div>
        <div className={ediotrHeaderStyle.documentControlsSection}>
          <ExportReview data={finalResult} documentId={match.params.id} />
        </div>
      </div>
      <div className={styles.mainPage}>
        <div className={styles.list}>
          <h3 className={`${styles.label} ${styles.sticky} mb-2`}>{t('aireview:content')}</h3>
          {finalResult.map((obj, key) => (
            <PrintThumbnail
              item={obj}
              key={key}
              onClick={setSelectedReview}
              active={JSON.stringify(selectedReview) === JSON.stringify(obj)}
            />
          ))}
        </div>
        {selectedReview && (
          <Editor
            key={selectedReview.name}
            content={selectedReview.review}
            tools={data.drawings.find(obj => obj.file === selectedReview.name)?.tools ?? []}
            id={match.params.id}
            fileName={selectedReview.name}
            className={styles.editorContner}
          />
        )}
        {data && selectedReview && (
          <div className={styles.leftSidebar}>
            <div>
              <h3 className={styles.label}>{t('common:subject')}</h3>
              <h3 className={styles.response}>{data.subject.title}</h3>
            </div>
            <div>
              <h3 className={styles.label}>{t('common:class')}</h3>
              <h3 className={styles.response}>{data.class.title}</h3>
            </div>
            <div>
              <h3 className={styles.label}>{t('aireview:document')}</h3>
              <h3 className={styles.response}>{selectedReview.name}</h3>
            </div>
            <div>
              <h3 className={styles.label}>{t('aireview:result')}</h3>
              <h3 className={styles.response}>{selectedReview.grade}%</h3>
            </div>
            <div className={styles.topDevider} />
            <div>
              <h3 className={styles.label}>{t('aireview:notes')}</h3>
              <TextField
                placeholder={t('aireview:add_notes_placeholder')}
                rows={5}
                multiLine={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(Results);
